import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Request from "common/Request";
import Result from "./Result";
import {
  labelRangesRetrieval,
  parseResult,
  MAX_SERVICES_TO_FETCH,
  MAX_SERVICES_TO_SHOW,
} from "./api";
import ActionsContext from "common/ActionsContext";
import { atBqnTarget, safeStr } from "common/api";
import { Header } from "common/Header";
import BqnContext from "common/BqnContext";
import useHoursOfReturnView from "common/hooks/useHoursOfReturnView";

const ViewContainer = styled.div`
  min-height: 50vh;
  &.card {
    height: 100%;
  }
`;

const ContextMenu = ({ reloadSignal = "do-load" }) => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <li>
        <a
          onClick={() => actions.send(reloadSignal)}
          data-toggle="cardloading"
          data-loading-effect="pulse"
          title="Refresh"
        >
          <i className="material-icons">refresh</i>
        </a>
      </li>
    </ul>
  );
};

const speedInfoRetrieval = (hours, subscriberGroup, mode, bqnId) => {
  const cmdSubscriberGroup = subscriberGroup
    ? ` subscriber-groups name ${safeStr(subscriberGroup)}`
    : "";

  return ifCl.run(
    atBqnTarget(
      `show pkteng speed domain all hours ${hours} max ${MAX_SERVICES_TO_FETCH}${cmdSubscriberGroup}`,
      mode,
      bqnId,
    ),
    {
      timeout: DPI_STATS_TMOUT_MS,
    },
  );
};

// The parameters and the speeds of the BQN selected in the header, or of the
// whole cluster.
const acquireParamsAndData = (hours, subscriberGroup, mode, bqnId) =>
  Promise.all([
    labelRangesRetrieval(mode, bqnId),
    speedInfoRetrieval(hours, subscriberGroup, mode, bqnId),
  ])
    .then(([labelByRTT, speedInfo]) => {
      const result = parseResult(speedInfo, labelByRTT);
      result.download.services = result.download.services.slice(0, MAX_SERVICES_TO_SHOW);
      result.upload.services = result.upload.services.slice(0, MAX_SERVICES_TO_SHOW);
      return result;
    })
    .then((result) => ({ result }));

const TCPOComparison = ({
  subscriberGroup,
  reloadSignal = "do-load",
  bqnSelector = true,
  returnView,
}) => {
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(null);
  const hoursOfReturnView = useHoursOfReturnView(returnView);
  const [hours, setHours] = useState(hoursOfReturnView ?? 24);
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;

  const doLoad = (hours, subscriberGroup) => {
    setRequest(acquireParamsAndData(hours, subscriberGroup, mode, bqnId));
  };

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    doLoad(hours, subscriberGroup);
    return actions.recv(reloadSignal, () => doLoad(hours, subscriberGroup));
  }, [hours, subscriberGroup, mode, bqnId]);

  return (
    <ViewContainer className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
      <div className="card">
        <Header
          title="TCP Speed & Acceleration"
          subtitle="Comparison of TCP connection average speed with and without TCP Optimization (TCPO)."
          contextMenu={<ContextMenu reloadSignal={reloadSignal} />}
          backButton={returnView}
          bqnSelector={bqnSelector}
        />
        <div className="body flex-panel-column">
          <Request during={request}>
            {(response) => (
              <Result
                {...response}
                onHoursChange={(value) => setHours(value)}
                hours={hours}
              />
            )}
          </Request>
        </div>
      </div>
    </ViewContainer>
  );
};

export default TCPOComparison;
